import { defineStore } from 'pinia';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { createGeneration, EntityEditLock } from '@/modules/core/utils/entity-edit-lock.ts';
import { reportEditError } from '@/modules/core/utils/edit-error.util.ts';
import { type EditValidity, isEditValid, updateEditValidity } from '@/modules/core/composables/use-edit-context.ts';
import { usePreviewStore } from '@/modules/core/stores/preview.store.ts';
import { CounterpartyApi } from '@/modules/counterparty/api/counterparty.api.ts';
import { useCounterpartyRowStore } from '@/modules/counterparty/stores/counterparty-row.store.ts';
import { CounterpartyOptionsUtil } from '@/modules/counterparty/utils/counterparty-options.util.ts';
import { refreshCounterpartiesList } from '@/modules/counterparty/counterparty-master/utils/refresh-counterparties-list.util.ts';
import type { CounterpartyInternal } from '@/modules/counterparty/types/counterparty-internal.type.ts';
import type {
  CounterpartyEditBlock,
  CounterpartyEditFallbackLabels,
  CounterpartyEditFormData,
} from '@/modules/counterparty/counterparty-edit/types/counterparty-edit-form.type.ts';
import { createEditForm, toEditParams } from '@/modules/counterparty/counterparty-edit/utils/counterparty-edit-form.ts';
import { publishCounterpartyUpdated } from '@/modules/counterparty/composables/counterparty-update-sync.ts';

type SessionState = {
  counterpartyId: number | null;
  isEditing: boolean;
  isLoading: boolean;
  isSaving: boolean;
  validity: EditValidity<CounterpartyEditBlock>;
  showErrors: boolean;
};

type State = SessionState & {
  snapshot: CounterpartyInternal | null;
  form: CounterpartyEditFormData | null;
};

const createSessionState = (): SessionState => ({
  counterpartyId: null,
  isEditing: false,
  isLoading: false,
  isSaving: false,
  validity: {},
  showErrors: false,
});

const createState = (): State => ({
  ...createSessionState(),
  snapshot: null,
  form: null,
});

const editLock = new EntityEditLock(TableId.Counterparties, {
  locked: 'counterparty.edit.notification.locked',
  lockLost: 'counterparty.edit.notification.lockLost',
  lockRenewError: 'counterparty.edit.notification.lockRenewError',
  editingElsewhere: 'counterparty.edit.notification.editingElsewhere',
});

const nextGeneration = createGeneration();

function onlyNumberCodes(options: Option[]) {
  return options.filter((option): option is Option<number> => typeof option.code === 'number');
}

function getFallbackLabels(counterpartyId: number): CounterpartyEditFallbackLabels {
  const { item } = useCounterpartyRowStore();

  if (item['CLIENT_ID'] !== counterpartyId) {
    return {};
  }

  const label = (field: string) => (typeof item[field] === 'string' ? item[field] : undefined);

  return {
    type: label('Тип'),
    legalForm: label('ПФ'),
    manager: label('Менеджер'),
    segment: label('Сегмент'),
    region: label('Регион'),
  };
}

async function loadSession(counterpartyId: number) {
  const { signal } = new AbortController();
  const [snapshot, types, legalForms, managers, segments, regions] = await Promise.all([
    CounterpartyApi.getInternal(counterpartyId),
    CounterpartyApi.getTypeOptions(signal),
    CounterpartyOptionsUtil.legalFormsById(signal),
    CounterpartyOptionsUtil.managersById(signal),
    CounterpartyOptionsUtil.segmentsById(signal),
    CounterpartyOptionsUtil.regionsById(signal),
  ]);

  const options = {
    types,
    legalForms: onlyNumberCodes(legalForms),
    managers: onlyNumberCodes(managers),
    segments: onlyNumberCodes(segments),
    regions: onlyNumberCodes(regions),
  };

  return { snapshot, form: createEditForm(snapshot, options, getFallbackLabels(counterpartyId)) };
}

export const useCounterpartyEditStore = defineStore(StoreId.CounterpartyEdit, {
  state: createState,
  getters: {
    isValid: (state) => isEditValid(state.validity),
    isEditingCounterparty: (state) => (counterpartyId: number) =>
      state.isEditing && state.counterpartyId === counterpartyId,
  },
  actions: {
    async open(counterpartyId: number) {
      if (this.isEditing || this.isLoading) {
        return;
      }

      const isCurrent = nextGeneration();

      this.isLoading = true;

      try {
        const session = await editLock.acquire(
          counterpartyId,
          isCurrent,
          () => loadSession(counterpartyId),
          () => void this.close({ releaseLock: false })
        );

        if (session) {
          this.$patch((state) => {
            Object.assign(state, { ...session.data, counterpartyId, isEditing: true });
          });
        }
      } catch (error) {
        if (isCurrent()) {
          this.$reset();
          reportEditError('counterparty.edit.notification.openError', error);
        }
      } finally {
        if (isCurrent()) {
          this.isLoading = false;
        }
      }
    },

    patchForm(patch: Partial<CounterpartyEditFormData>) {
      if (this.isEditing && this.form) {
        Object.assign(this.form, patch);
      }
    },

    setValidity(block: CounterpartyEditBlock, isValid: boolean | null) {
      this.validity = updateEditValidity(this.validity, block, isValid);
    },

    async save() {
      if (!this.isEditing || !this.form || !this.snapshot || this.isSaving) {
        return false;
      }

      if (!this.isValid) {
        this.showErrors = true;
        return false;
      }

      const { snapshot, form } = this;

      this.isSaving = true;

      try {
        if (!(await editLock.isFree(snapshot.id))) {
          this.isSaving = false;
          return false;
        }

        await CounterpartyApi.edit(toEditParams(snapshot, form));
      } catch (error) {
        reportEditError('counterparty.edit.notification.saveError', error);
        this.isSaving = false;
        return false;
      }

      notification.success('counterparty.edit.notification.saved');

      await this.close();
      usePreviewStore().refreshPreviewAction();
      void refreshCounterpartiesList();
      publishCounterpartyUpdated(snapshot.id);

      return true;
    },

    async close({ releaseLock: shouldReleaseLock = true }: { releaseLock?: boolean } = {}) {
      const { counterpartyId } = this;

      nextGeneration();
      this.$patch((state) => {
        Object.assign(state, createSessionState());
      });

      await editLock.close(counterpartyId, shouldReleaseLock);
    },
  },
});
